<template>
  <v-dialog
    content-class="sequence-preview"
    v-model="showDialog"
    persistent
    max-width="400"
    @click:outside="onClickOutside"
    @keydown="onKeydown"
  >
    <v-btn
      color="bh-white"
      small
      rounded
      height="24"
      width="24"
      min-width="24"
      max-width="24"
      elevation="0"
      class="mt-6 mr-6 pa-0"
      @click="closeDialog"
    >
      <v-icon color="accent">mdi-close</v-icon>
    </v-btn>
    <v-card v-if="sequence">
      <v-card-title class="headline">
        <div class="sequence-preview__title">
          <h5>{{ sequence.title }}</h5>
          <p>{{ sequence.description }}</p>
        </div>
      </v-card-title>

      <v-card-text>
        <p v-if="sequence.itemsCount && sequence.dayCount">
          {{ sequence.dayCount }} day{{
            sequence.dayCount > 1 ? 's' : ''
          }}
          containing {{ sequence.itemsCount }}
          {{ sequence.itemsCount > 1 ? 'activities' : 'activity' }}
        </p>
        Contains:
        <v-tabs
          class="sequence-preview__tabs"
          v-model="tab"
          color="accent"
          show-arrows
        >
          <v-tabs-slider></v-tabs-slider>
          <v-tab v-for="week in weeks" :key="week.label" @change="tab2 = 0">
            {{ week.label }}
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab">
          <v-tab-item
            class="sequence-preview__tab quick-view"
            v-for="week in weeks"
            :key="week.label"
          >
            <v-tabs v-model="tab2" color="accent" show-arrows>
              <v-tab v-for="day in week.days" :key="'day' + day.day">
                D{{ day.day }}
              </v-tab>
            </v-tabs>
            <v-tabs-items v-model="tab2">
              <v-tab-item v-for="day in week.days" :key="'day' + day.day">
                <exercise-tile
                  v-for="item in day.items"
                  :key="'item' + item.id"
                  :id="item.id"
                  :title="item.title"
                  :description="item.description"
                  :additional-media="item.additionalMedia"
                  :media="item.media"
                  :multi-thumbnail="
                    item.multiThumbnail ? item.multiThumbnail : null
                  "
                  :type="item.type"
                  :editable="false"
                  :readonly="true"
                  :hideMenu="true"
                  :mobile-static-media="item.mobileStaticMedia"
                />
              </v-tab-item>
            </v-tabs-items>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'
import ExerciseTile from '@/components/exercises/ExerciseTile.vue'

export default {
  name: 'PreviewSequenceDialog',
  components: {
    ExerciseTile,
  },
  props: {
    showDialog: {
      type: Boolean,
      default: false,
    },
    sequence: {
      type: [Object, Boolean],
      default: () => {
        return {}
      },
    },
  },
  watch: {},
  data() {
    return {
      tab: 0,
      tab2: 0,
    }
  },
  computed: {
    weeks() {
      let weeks = []
      this.sequence?.days?.forEach(day => {
        const weekIndex = Math.floor((day.day - 1) / 7)
        if (!weeks[weekIndex]) {
          weeks[weekIndex] = { label: `Week ${weekIndex + 1}`, days: [] }
        }
        weeks[weekIndex].days.push(day)
      })

      weeks = weeks
        .map(week => ({
          ...week,
          days: week.days.map(day => ({
            ...day,
            items: day.items.map(item => {
              let source
              if (item.type === 'EXERCISE') {
                source = item.exercise
              } else if (item.type === 'SERIES') {
                source = item.series
              } else {
                source = item.activity
              }
              return {
                ...item,
                title: source.title,
                description: source.description,
                media: source.media,
                additionalMedia: source.additionalMedia,
                mobileStaticMedia: source.mobileStaticMedia,
                ...(item.type === 'SERIES' && {
                  multiThumbnail: source.exerciseDefinitions,
                }),
              }
            }),
          })),
        }))
        .filter(Boolean)

      return weeks
    },
  },
  methods: {
    ...mapActions('resourcesStates', [
      'downloadResourceByUrl',
      'checkFileAvailable',
      'getResourceInfo',
    ]),
    closeDialog() {
      this.tab = 0
      this.$emit('close-dialog')
    },
    onClickOutside() {
      this.closeDialog()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted) this.closeDialog()
    },
    previewResource() {
      this.$emit('preview-media', this.sequence.media)
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';
.sequence-preview {
  position: relative;
  & > .v-btn {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
  }
  &__thumbnail {
    width: 64px;
    height: 64px;
    object-fit: cover;
    border-radius: 8px;
    &--wrapper {
      position: relative;
      width: 64px;
      height: 64px;
    }
    &--icon {
      position: absolute;
      bottom: 2px;
      left: 2px;
      min-width: 20px;
      height: 20px;
      border-radius: 6px;
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 3;
      .v-icon {
        color: $bh-white;
        font-size: 12px;
      }
      span {
        font-size: 0.6rem;
        padding-right: 2px;
        color: $bh-white;
        font-weight: bold;
        padding-top: 2px;
      }
    }
  }
  .headline {
    flex-wrap: nowrap;
  }
  &__title {
    margin-top: 8px;
    margin-bottom: 8px;
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding-right: 32px;
    flex-grow: 0;
    h5 {
      color: $bh-black;
      margin: 0;
    }
    p {
      font-weight: 400;
      font-size: 0.8rem;
      line-height: 1.2;
      margin: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
    }
  }
  &__tab {
    padding-top: 8px;
    p {
      margin: 0;
    }
    &.quick-view {
      .v-image {
        margin-bottom: 8px;
        .gradient {
          background-image: linear-gradient(
            to top,
            rgba(0, 0, 0, 1) 20%,
            transparent 100%
          );
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 1;
        }
      }
    }
  }
  &__expand {
    position: absolute;
    bottom: 16px;
    right: 8px;
    z-index: 3;
    .v-icon {
      &::before {
        color: $bh-white;
      }
    }
  }
  &__desc {
    position: absolute;
    bottom: 16px;
    width: 100%;
    left: 0;
    text-align: center;
    color: $bh-white;
    &--wrapper {
      position: relative;
    }
    p {
      color: $bh-white;
      padding: 8px;
      z-index: 2;
      overflow: auto;
      font-size: 1rem;
      font-weight: 700;
      max-height: 100%;
    }
    .v-btn {
      margin-top: 8px;
    }
  }
  &__attributes {
    position: absolute;
    top: 8px;
    left: 8px;
    right: 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    div {
      background: $bh-blue;
      min-width: 64px;
      height: 64px;
      border-radius: 8px;
      color: $bh-white;
      padding: 4px 12px;
      font-size: 0.8rem;
      strong {
        font-size: 26px;
        line-height: 36px;
        display: block;
      }
    }
  }
  &__rating {
    display: flex;
    align-items: center;
    margin-top: 3px;
    h3 {
      margin: 0 12px 0 0;
      padding-top: 2px;
      color: $bh-black;
    }
    span {
      font-size: 0.8rem;
      line-height: 1;
      font-weight: 400;
      letter-spacing: 0;
    }
  }
  &__participants--list {
    margin-top: 16px;
    .v-list-item {
      padding: 0;
    }
  }
  &__participant {
    &--thumbnail {
      width: 40px;
      height: 40px;
      object-fit: cover;
      border-radius: 50%;
    }
    &--text {
      flex-grow: 1;
      margin-left: 16px;
      h5 {
        margin: 0;
      }
      p {
        margin: 0;
        color: $bh-gray;
        font-size: 0.8rem;
      }
    }
  }
}
</style>
